/**
 * Reset Password Page Component
 * Password reset interface with token validation and password confirmation
 */

import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useNotifications } from '../../context/NotificationContext';
import config from '../../config/environment';
import '../Login/Login.css';

const ResetPassword: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { showError, showSuccess } = useNotifications();
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isValidToken, setIsValidToken] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    if (!token) {
      setIsValidToken(false);
      showError('Invalid Link', 'This password reset link is invalid.');
    }
  }, [token, showError]);

  const validatePassword = (pwd: string): string | null => {
    if (pwd.length < 6) {
      return 'Password must be at least 6 characters long';
    }
    if (!/[A-Z]/.test(pwd)) {
      return 'Password must contain at least one uppercase letter';
    }
    if (!/[a-z]/.test(pwd)) {
      return 'Password must contain at least one lowercase letter';
    }
    if (!/[0-9]/.test(pwd)) {
      return 'Password must contain at least one number';
    }
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!password || !confirmPassword) {
      showError('Missing Fields', 'Please fill in all fields');
      return;
    }

    if (password !== confirmPassword) {
      showError('Password Mismatch', 'Passwords do not match');
      return;
    }

    const validationError = validatePassword(password);
    if (validationError) {
      showError('Invalid Password', validationError);
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(`${config.api.baseUrl}/api/auth/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          token,
          newPassword: password 
        }),
      });

      const data = await response.json();

      if (response.ok) {
        showSuccess('Password Reset', 'Your password has been reset successfully!');
        setTimeout(() => {
          navigate('/login');
        }, 2000);
      } else {
        showError('Reset Failed', data.message || 'Failed to reset password');
        if (response.status === 400 || response.status === 404) {
          setIsValidToken(false);
        }
      }
    } catch (error) {
      console.error('Reset password error:', error);
      showError('Network Error', 'Please try again later');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isValidToken) {
    return (
      <div className="login-container">
        <div className="login-card">
          <div className="login-header">
            <h1>Invalid Link</h1>
            <p>This password reset link is invalid or has expired</p>
          </div>

          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>⚠️</div>
            <p style={{ color: '#718096', fontSize: '14px', marginBottom: '24px' }}>
              Please request a new password reset link
            </p>
            
            <Link to="/forgot-password" className="login-button" style={{ textDecoration: 'none' }}>
              Request New Link
            </Link>
          </div>

          <div className="login-links">
            <Link to="/login" className="forgot-password-link">
              ← Back to Login
            </Link>
          </div>

          <div className="login-footer">
            <p>&copy; 2024 Kadi Ni Tidi Platform. All rights reserved.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-header">
          <h1>Reset Password</h1>
          <p>Choose a strong password for your account</p>
        </div>

        <form onSubmit={handleSubmit} className="login-form">
          <div className="form-group">
            <label htmlFor="password">New Password</label>
            <div className="password-input-container">
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                name="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter new password"
                disabled={isSubmitting}
                autoComplete="new-password"
                required
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
                disabled={isSubmitting}
              >
                {showPassword ? '👁️' : '🙈'}
              </button>
            </div>
            <span style={{ fontSize: '11px', color: '#718096', marginTop: '2px' }}>
              Must be 6+ characters with uppercase, lowercase, and number
            </span>
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm Password</label>
            <div className="password-input-container">
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                id="confirmPassword"
                name="confirmPassword"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
                disabled={isSubmitting}
                autoComplete="new-password"
                required
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                disabled={isSubmitting}
              >
                {showConfirmPassword ? '👁️' : '🙈'}
              </button>
            </div>
          </div>

          {password && confirmPassword && password !== confirmPassword && (
            <div className="error-message">
              Passwords do not match
            </div>
          )}

          <button
            type="submit"
            className="login-button"
            disabled={isSubmitting || !password || !confirmPassword || password !== confirmPassword}
          >
            {isSubmitting ? (
              <>
                <span className="spinner-small"></span>
                Resetting...
              </>
            ) : (
              'Reset Password'
            )}
          </button>
        </form>

        <div className="login-links">
          <Link to="/login" className="forgot-password-link">
            ← Back to Login
          </Link>
        </div>

        <div className="login-footer">
          <p>&copy; 2024 Kadi Ni Tidi Platform. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
